import { FC, useState } from 'react';

import { Form, Input, Modal } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import { buildMembershipMessage, REGISTRATION_FILTER_SECTIONS, REGISTRATION_SECTION_ID } from './utils';
import { IFilter } from '../../../../../app/providers/redux/slices/reports/params/filter/types';
import { getCookie } from '../../../../../helpers/utils/cookies';
import { showErrorMessage, showInfoNotification, showSuccessNotification, getApiErrorMessage } from '../../../../../helpers/utils/ui';
import { unApi } from '../../../api/endpoints/reports/unApi';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import Loading from '../cohort/components/Loading';
import Filter from '../params/filter';

type Props = {
  visible: boolean;
  onClose: () => void;
  // Данные исходной группы для копирования (режим «дублировать»)
  initialData?: any;
  duplicate?: boolean;
};

/**
 * Актуальный pathname для заголовка Pathname запроса (на дашборде берём из cookie).
 */
const getReportPathname = (): string =>
  window.location.pathname.includes('dashboard')
    ? getCookie('dashboardPathname') || window.location.pathname
    : window.location.pathname;

/**
 * Разбирает filter_json группы в массив фильтров (гарантирует массив).
 */
const parseGroupFilters = (filterJson: unknown): IFilter[] => {
  if (!filterJson) return [];
  try {
    const parsed = typeof filterJson === 'string' ? JSON.parse(filterJson) : filterJson;
    if (Array.isArray(parsed)) return parsed;
    return parsed ? [ parsed ] : [];
  } catch (err) {
    console.error('Ошибка разбора filter_json группы:', err);
    return [];
  }
};

/**
 * Модалка создания «Группы регистраций» (а также копирования существующей через initialData/duplicate).
 * Эталон механики — CreateMetricsModal (локальные фильтры, рефетч списка после сохранения).
 */
const CreateGroupModal: FC<Props> = ({ visible, onClose, initialData, duplicate }) => {
  const [ form ] = Form.useForm();
  const { getCustomGroups } = useActions();
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);

  const [ isLoading, setIsLoading ] = useState(false);
  // Локальные фильтры регистрации (не трогаем глобальные filters)
  const [ groupFilters, setGroupFilters ] = useState<IFilter[]>(
    duplicate ? parseGroupFilters(initialData?.filter_json) : []
  );

  const reportPathname = getReportPathname();

  const handleSubmit = async () => {
    if (!selectedSchool?.id) return;

    let values: { name: string; description?: string };
    try {
      values = await form.validateFields();
    } catch {
      return; // ошибки валидации подсветит форма
    }

    if (!Array.isArray(groupFilters) || groupFilters.length === 0) {
      showErrorMessage('Задайте хотя бы одно условие фильтра регистрации');
      return;
    }

    const body = {
      section_id: REGISTRATION_SECTION_ID,
      name: values.name.trim(),
      filter_json: JSON.stringify(groupFilters),
      sort: 0,
      description: values.description?.trim() || null,
    };

    try {
      setIsLoading(true);
      const response = await unApi.addCustomGroup(body, reportPathname);
      if (response.status === 200 || response.status === 201) {
        // Инфо-окно наполнения связи «группа ↔ регистрация» (кол-во + время)
        const membership = response.data?.data?.membership;
        const { kind, text } = buildMembershipMessage(membership, 'create');
        if (kind === 'warning') showInfoNotification(text);
        else showSuccessNotification(text);
        // Рефетч списка групп, затем закрытие
        setTimeout(
          () =>
            getCustomGroups(selectedSchool.id, reportPathname, () => {
              setIsLoading(false);
              setTimeout(() => onClose(), 100);
            }, true),
          400
        );
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Ошибка создания группы:', err);
      showErrorMessage(getApiErrorMessage(err, 'Произошла ошибка во время создания группы.'));
      setIsLoading(false);
    }
  };

  return (
    <Modal
      style={{ top: 20 }}
      width={'55%'}
      title={<span style={{ fontWeight: 700 }}>{duplicate ? 'Копирование группы' : 'Создание группы регистраций'}</span>}
      onCancel={onClose}
      open={visible}
      okText="Сохранить"
      cancelText="Отмена"
      onOk={handleSubmit}
    >
      {/* Подпись «Сохраняем…», а не «Наполняем…»: наполнение идёт на бэке только
          после успешного сохранения. Если бэк отклонит (напр. дубль имени, 422),
          «Наполняем группу…» вводило бы в заблуждение — группа не создаётся. */}
      <Loading loading={isLoading} tip="Сохраняем группу…">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            name: duplicate ? `${initialData?.name ?? '-'} (копия)` : '',
            description: initialData?.description ?? '',
          }}
        >
          {/* Название и описание в одну строку — как в окне настроек кастомных метрик */}
          <div style={{ display: 'flex', columnGap: 20 }}>
            <Form.Item
              style={{ width: '65%' }}
              label={<span style={{ fontWeight: 700 }}>Название группы</span>}
              name="name"
              rules={[ { required: true, message: 'Введите название группы' }, { max: 100, message: 'Не более 100 символов' } ]}
            >
              <Input maxLength={100} />
            </Form.Item>

            <Form.Item
              style={{ width: '35%' }}
              label={<span style={{ fontWeight: 700 }}>Описание</span>}
              name="description"
              rules={[ { max: 300, message: 'Не более 300 символов' } ]}
            >
              <TextArea maxLength={300} style={{ height: '32px', width: '100%' }} />
            </Form.Item>
          </div>

          <div style={{ fontWeight: 700, marginBottom: 8 }}>Фильтр регистрации</div>
          <div style={{ marginBottom: 16 }}>
            <Filter
              notParams={true}
              withSegment={false}
              filtersFilter={REGISTRATION_FILTER_SECTIONS}
              isCompare={false}
              withRefresh={false}
              localFilters={groupFilters}
              setLocalFilters={setGroupFilters}
            />
          </div>
        </Form>
      </Loading>
    </Modal>
  );
};

export default CreateGroupModal;
